import React, { useCallback, useState } from 'react';
import {
    Stepper, Step, StepLabel,
    Button, Typography, Box, TextField,
    Paper, IconButton, Dialog,
    DialogTitle, DialogContent, DialogActions,
    List, ListItem, ListItemText, Divider,
    Chip,
    Card,
    CardContent,
    Grid,
    Tooltip,
    Stack,
    CircularProgress
} from '@mui/material';
import ErrorIcon from '@mui/icons-material/ErrorOutline';
import WarningIcon from '@mui/icons-material/WarningAmber';
import ReportIcon from '@mui/icons-material/ReportProblemOutlined';
import HelpIcon from '@mui/icons-material/HelpOutline';
import InfoIcon from '@mui/icons-material/Info';
import CheckCircleIcon from '@mui/icons-material/CheckCircleOutline';
import { auth } from '../../firebase/firebase';
import { Add, Delete, TextSnippet, UploadFile } from '@mui/icons-material';
import DoiAnalysisLink from '../DoiAnalysisLink';
import { useDropzone } from 'react-dropzone';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';


interface Reference {
    rawInput: string;
    reference?: {
        doi: string;
        title: string;
        publicationDate: string;
    };
}

interface ApiResponse {
    referencesFound: {
        matchedReferences: Reference[];
        unmatchedReferences: Reference[];
    };
}

interface AnalysisResult {
    title: string;
    publicationDate: string;
    isRetracted: boolean;
    isRetractionNote: boolean;
    isHighRisk: boolean;
    isMediumRisk: boolean;
}

interface AnalysisItem {
    referenceDoi: string;
    doiFound: boolean;
    analysis: AnalysisResult | null;
}

type InputMethod = 'text' | 'file' | null;

const ReferenceWizard: React.FC = () => {
    const [activeStep, setActiveStep] = useState(0);
    const [inputMethod, setInputMethod] = useState<InputMethod>(null);
    const [file, setFile] = useState<File | null>(null);
    const [textInput, setTextInput] = useState('');
    const [matched, setMatched] = useState<Reference[]>([]);
    const [unmatched, setUnmatched] = useState<Reference[]>([]);
    const [doiDialogOpen, setDoiDialogOpen] = useState(false);
    const [newDoi, setNewDoi] = useState('');
    const [analysis, setAnalysis] = useState<AnalysisItem[]>([]);
    const [previewingIsRunning, setPreviewingIsRunning] = useState<boolean>(false);
    const [analysisIsRunning, setAnalysisIsRunning] = useState<boolean>(false);

    const onDrop = useCallback((acceptedFiles: File[]) => {
        console.log('Files dropped:', acceptedFiles);
        setFile(acceptedFiles[0])
        // Handle the files here (e.g. upload or preview)
    }, []);

    const { getRootProps, getInputProps, open, isDragActive } = useDropzone({
        onDrop,
        noClick: true,
        noKeyboard: true,
        accept: { 'application/pdf': ['.pdf'] }
        // onChange={(e) => e.target.files && setFile(e.target.files[0])}
    });


    const steps = ['Choose input method', 'Provide your references', 'Preview references', 'View Argos analysis'];

    const handlePreview = async () => {
        setPreviewingIsRunning(true)
        let authToken = await auth.currentUser?.getIdToken(true)
        try {
            let response;
            if (inputMethod === 'file' && file) {
                const formData = new FormData();
                formData.append('file', file);
                response = await fetch('https://document-api-dot-singular-ally-404614.ew.r.appspot.com/doc/reference-analysis', {
                    method: 'POST',
                    body: formData,
                    headers: {
                        'Authorization': 'Bearer ' + authToken
                    }
                });
            } else if (inputMethod === 'text' && textInput.trim()) {
                response = await fetch('https://document-api-dot-singular-ally-404614.ew.r.appspot.com/section/reference-analysis', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': 'Bearer ' + authToken
                    },
                    body: JSON.stringify({ referenceSection: textInput }),
                });
            } else {
                alert('Provide a valid input.');
                return;
            }

            if (!response.ok) throw new Error('Failed to analyze references');
            const data: ApiResponse = await response.json();
            setMatched(data.referencesFound.matchedReferences);
            setUnmatched(data.referencesFound.unmatchedReferences);
            setActiveStep(2);
            setPreviewingIsRunning(false)
        } catch (err) {
            alert('Error analyzing references.');
            console.error(err);
            setPreviewingIsRunning(false)
        }
    };

    const renderChip = (item: AnalysisItem) => {
        if (!item.doiFound) {
            return (
                <Chip
                    icon={<HelpIcon />}
                    label="DOI outside Argos scope"
                    color="info"
                    variant="outlined"
                />
            );
        }

        if (item.analysis) {
            if (item.analysis.isRetracted) {
                return (
                    <Chip
                        icon={<ErrorIcon />}
                        label="Retracted"
                        color="error"
                    />
                );
            }
            if (item.analysis.isHighRisk) {
                return (
                    <Chip
                        icon={<WarningIcon />}
                        label="High Risk"
                        color="error"
                    />
                );
            }
            if (item.analysis.isMediumRisk) {
                return (
                    <Chip
                        icon={<ReportIcon />}
                        label="Medium Risk"
                        color="warning"
                        variant="outlined"
                    />
                );
            }
        }
        return (
            <Chip
                icon={<CheckCircleIcon />}
                label="Low Risk"
                color="success"
                variant="outlined"
            />
        );
    };

    const handleAnalysis = async () => {
        setAnalysisIsRunning(true)
        let authToken = await auth.currentUser?.getIdToken(true)
        try {
            await fetch('https://document-api-dot-singular-ally-404614.ew.r.appspot.com/doi/reference-analysis', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + authToken
                },
                body: JSON.stringify({
                    references: matched
                        .map(ref => ref.reference!!.doi)
                })
            }).then((res) => {
                if (!res.ok) throw new Error(`HTTP ${res.status}`);
                return res.json();
            })
                .then((json) => {
                    let data = json.analysis
                    setAnalysis(data);
                    setActiveStep(3);
                    setAnalysisIsRunning(false)
                })

        } catch (err) {
            alert('Sorry, there was an error analyzing your references, please try again later or contact hello@scitility.com');
            console.error(err);
            setAnalysisIsRunning(false)
        }
    };

    const removeMatched = (index: number) => {
        const updated = [...matched];
        updated.splice(index, 1);
        setMatched(updated);
    };

    const addManualReference = () => {
        if (newDoi.trim()) {
            const newRef: Reference = {
                rawInput: `Manual entry: ${newDoi}`,
                reference: {
                    doi: newDoi,
                    title: 'Manually added (title not resolved)',
                    publicationDate: 'N/A',
                },
            };
            setMatched([...matched, newRef]);
            setDoiDialogOpen(false);
            setNewDoi('');
        }
    };

    return (
        <>
            <div id="author-analysis" />
            <Typography gutterBottom variant="h3" component="div" sx={{ color: '#003366', fontWeight: 'bold' }}>
                Reference analysis
                <Tooltip title="Paste your reference or upload an article to instantly detect potentially problematic or retracted citations" placement="top-start">
                    <InfoIcon color="primary" fontSize="small" style={{ verticalAlign: 'top' }} />
                </Tooltip>
            </Typography>
            <Card variant="outlined" sx={{ pt: 4, mt: -1 }}>
                <CardContent>
                    <Stepper activeStep={activeStep} alternativeLabel>
                        {steps.map((label, idx) => (
                            <Step key={idx}>
                                <StepLabel>{label}</StepLabel>
                            </Step>
                        ))}
                    </Stepper>
                    <Box my={4}>
                        {activeStep === 0 && (
                            <Stack direction="row" spacing={4} sx={{ textAlign: "center", justifyContent: "center", paddingBottom: 2, paddingLeft: 2, paddingRight: 2, paddingTop: 4 }}>
                                <Button
                                    variant="contained"
                                    startIcon={<TextSnippet />}
                                    onClick={() => {
                                        setInputMethod('text');
                                        setActiveStep(1);
                                    }}
                                >
                                    Paste your references
                                </Button>

                                <Button
                                    variant="contained"
                                    startIcon={<UploadFile />}
                                    onClick={() => {
                                        setInputMethod('file');
                                        setActiveStep(1);
                                    }}
                                >
                                    Upload your PDF
                                </Button>

                            </Stack>
                        )}

                        {activeStep === 1 && (
                            <Box>
                                {previewingIsRunning &&
                                    <Box
                                        sx={{
                                            position: 'fixed',
                                            top: 0,
                                            left: 0,
                                            zIndex: 1300, // Higher than app content
                                            width: '100vw',
                                            height: '100vh',
                                            bgcolor: 'rgba(128,128,128,0.5)', // grey out
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            pointerEvents: 'all', // ensures it blocks interaction
                                        }}
                                    >
                                        <Box sx={{ backgroundColor: 'white', padding: 10, borderRadius: 2 }}>
                                            <Typography>Argos is analysing your PDF and extracting your references, please don't close this window.</Typography>
                                            <CircularProgress color="inherit" sx={{ mt: 4 }} />
                                        </Box>
                                    </Box>
                                }
                                {inputMethod === 'text' ? (
                                    <TextField
                                        fullWidth
                                        multiline
                                        rows={6}
                                        label="Paste your DOIs (recommended method), one DOI per line. Alternatively, you can paste your entire reference section. Please try to keep one reference per line."
                                        value={textInput}
                                        onChange={(e) => setTextInput(e.target.value)}
                                    />
                                ) : (
                                    <Stack direction="row" spacing={4} sx={{ textAlign: "center", justifyContent: "center", paddingBottom: 2, paddingLeft: 2, paddingRight: 2, paddingTop: 4 }}>
                                        <Paper
                                            {...getRootProps()}
                                            sx={{
                                                border: '2px dashed #aaa',
                                                borderRadius: 2,
                                                padding: 4,
                                                width: 400,
                                                textAlign: 'center',
                                                bgcolor: '#fff',
                                                transition: 'background-color 0.2s',
                                                ...(isDragActive && {
                                                    bgcolor: '#e3f2fd',
                                                    borderColor: '#1976d2',
                                                }),
                                            }}
                                        >
                                            {!file &&
                                                <><input {...getInputProps()} />
                                                    <Stack spacing={2} alignItems="center">
                                                        <CloudUploadIcon color="primary" fontSize="large" />
                                                        <Typography variant="h6">
                                                            {isDragActive ? 'Drop your PDF file here' : 'Drag and drop your PDF file here'}
                                                        </Typography>
                                                        <Typography variant="body2" color="textSecondary">
                                                            or
                                                        </Typography>
                                                        <Button variant="contained" onClick={open}>
                                                            Choose Files
                                                        </Button>
                                                    </Stack></>
                                            }
                                            {file && <Stack spacing={2} alignItems="center">
                                                <CloudUploadIcon />
                                                <Typography mt={1}>
                                                    {file.name}
                                                </Typography>
                                                <Typography variant="body2" color="textSecondary">
                                                    or
                                                </Typography>
                                                <Button variant="contained" onClick={() => setFile(null)}>
                                                    Choose another file
                                                </Button>
                                            </Stack>
                                            }
                                        </Paper>
                                    </Stack>
                                )}
                                <Stack direction="row" spacing={4} sx={{ textAlign: "center", justifyContent: "center", paddingBottom: 2, paddingLeft: 2, paddingRight: 2, paddingTop: 4 }}>
                                    <Button variant="contained" onClick={handlePreview}
                                        disabled={(inputMethod === 'file' && !file) || previewingIsRunning}>
                                        Submit
                                    </Button>
                                    <Button sx={{ ml: 2 }} onClick={() => setActiveStep(0)} disabled={previewingIsRunning}>
                                        Back
                                    </Button>
                                </Stack>
                            </Box>
                        )}

                        {activeStep === 2 && (
                            <>
                                {analysisIsRunning &&
                                    <Stack direction="row" spacing={4} sx={{ textAlign: "center", justifyContent: "center", paddingBottom: 2, paddingLeft: 2, paddingRight: 2, paddingTop: 4 }}>
                                        <Box
                                            sx={{
                                                position: 'fixed',
                                                top: 0,
                                                left: 0,
                                                zIndex: 1300, // Higher than app content
                                                width: '100vw',
                                                height: '100vh',
                                                bgcolor: 'rgba(128,128,128,0.5)', // grey out
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                pointerEvents: 'all', // ensures it blocks interaction
                                            }}
                                        >
                                            <Box sx={{ backgroundColor: 'white', padding: 10, borderRadius: 2 }}>
                                                <Typography>Argos is analysing your references, please don't close this window.</Typography>
                                                <CircularProgress color="inherit" sx={{ mt: 4 }} />
                                            </Box>
                                        </Box>
                                    </Stack>
                                }
                                <Box>
                                    <Typography variant="h6">Matched References ({matched.length})</Typography>
                                    {matched.length === 0 && <Typography>No matched references.</Typography>}
                                    <List>
                                        {matched.map((ref, i) => (
                                            <Paper key={i} sx={{ my: 1, p: 2 }}>
                                                <ListItem
                                                    secondaryAction={
                                                        <IconButton edge="end" onClick={() => removeMatched(i)}>
                                                            <Delete />
                                                        </IconButton>
                                                    }
                                                >
                                                    <ListItemText
                                                        primary={ref.reference?.title || 'Unknown Title'}
                                                        secondary={
                                                            <>
                                                                <div><strong>DOI:</strong> {ref.reference?.doi}</div>
                                                                <div><strong>Publication Date:</strong> {ref.reference?.publicationDate}</div>
                                                                <div><strong>Raw:</strong> {ref.rawInput}</div>
                                                            </>
                                                        }
                                                    />
                                                </ListItem>
                                            </Paper>
                                        ))}
                                    </List>

                                    <Box mt={2}>
                                        <Button
                                            variant="outlined"
                                            startIcon={<Add />}
                                            onClick={() => setDoiDialogOpen(true)}
                                        >
                                            Add Reference by DOI
                                        </Button>
                                    </Box>

                                    <Divider sx={{ my: 3 }} />
                                    <Typography variant="h6">Unmatched References ({unmatched.length})</Typography>
                                    {unmatched.length === 0 && <Typography>No unmatched references.</Typography>}
                                    <List>
                                        {unmatched.map((ref, i) => (
                                            <Paper key={i} sx={{ my: 1, p: 2 }}>
                                                <ListItem>
                                                    <ListItemText
                                                        primary="Unmatched Reference"
                                                        secondary={<div><strong>Raw:</strong> {ref.rawInput}</div>}
                                                    />
                                                </ListItem>
                                            </Paper>
                                        ))}
                                    </List>
                                    <Box mt={3}>
                                        <Button variant="contained" onClick={handleAnalysis}>
                                            Submit
                                        </Button>
                                        <Button sx={{ ml: 2 }} onClick={() => setActiveStep(1)}>
                                            Back
                                        </Button>
                                    </Box>
                                </Box>
                            </>
                        )}

                        {activeStep === 3 &&
                            <Box sx={{ p: 4 }}>
                                <Typography variant="h4" gutterBottom>
                                    Reference Analysis Report
                                </Typography>
                                {analysis && (
                                    <Grid container spacing={3}>
                                        {analysis.map((item) => (
                                            <Grid item xs={12} sm={6} md={4} key={item.referenceDoi}>
                                                <Card
                                                    sx={{
                                                        background:
                                                            'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
                                                        borderRadius: 3,
                                                        boxShadow: 3,
                                                    }}
                                                >
                                                    <CardContent>
                                                        {item.doiFound &&
                                                            <>
                                                                <Typography variant="body1" gutterBottom>
                                                                    {item.analysis?.title}
                                                                </Typography>
                                                                <DoiAnalysisLink doi={item.referenceDoi} newRetractions={[]} showDoiInTitle={true}
                                                                    whenLoading={() => { }}
                                                                    whenLoaded={() => { }}
                                                                />
                                                                <Typography variant="caption" component='div'>Publication date: {item.analysis?.publicationDate}</Typography>
                                                            </>
                                                        }
                                                        {!item.doiFound &&
                                                            <Typography variant="subtitle2" gutterBottom>
                                                                {item.referenceDoi}
                                                            </Typography>
                                                        }
                                                        <Box sx={{ mt: 2 }}>{renderChip(item)}</Box>
                                                    </CardContent>
                                                </Card>
                                            </Grid>
                                        ))}
                                        <Grid item xs={12}>
                                            <Stack direction="row" spacing={4} sx={{ textAlign: "center", justifyContent: "center", paddingBottom: 2, paddingLeft: 2, paddingRight: 2, paddingTop: 4 }}>
                                                <Button variant="contained" sx={{ ml: 2 }} onClick={() => setActiveStep(0)}>
                                                    Do it again
                                                </Button>
                                                <Button sx={{ ml: 2 }} onClick={() => setActiveStep(2)}>
                                                    Back
                                                </Button>
                                            </Stack>
                                        </Grid>

                                    </Grid>
                                )}
                            </Box>
                        }
                    </Box>

                    <Dialog open={doiDialogOpen} onClose={() => setDoiDialogOpen(false)}>
                        <DialogTitle>Add Reference by DOI</DialogTitle>
                        <DialogContent>
                            <TextField
                                label="DOI"
                                fullWidth
                                value={newDoi}
                                onChange={(e) => setNewDoi(e.target.value)}
                            />
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={() => setDoiDialogOpen(false)}>Cancel</Button>
                            <Button variant="contained" onClick={addManualReference}>
                                Add
                            </Button>
                        </DialogActions>
                    </Dialog>
                </CardContent>
            </Card>
        </>
    );
};

export default ReferenceWizard;